<template>
  <div>
    <BaseHeader
      :title="$t('partner.title')"
      :style-variant="13"
    >
      <template
        v-if="!isPlatformBottimmo"
        v-slot:design
      >
        <HeaderDesign />
      </template>

      <template v-slot:desc>
        <p>
          {{ $t('partner.subtitle') }}
        </p>
      </template>
      <template
        v-if="filters"
        v-slot:content
      >
        <v-row class="pa-4">
          <v-col
            cols="12"
            lg="9"
          >
            <p>
              {{ $t('partner.body-texts.0') }}
            </p>
            <v-chip
              v-for="(filter, index) of filtersArray"
              :key="index"
              :variant="!filters[filter].isActive ? 'outlined' : 'flat'"
              class="ma-2"
              color="primary"
              rounded
              @click="() => toggleFilter(filter)"
            >
              <span :class="[filters[filter].isActive && 'text-white']">
                {{ $t(ReadablePartnerTags[filter]) }} ({{ filters[filter].count }})
              </span>
            </v-chip>
          </v-col>
        </v-row>
      </template>
    </BaseHeader>
    <v-row v-if="offers.length > 0">
      <v-col
        v-for="voucher in offers"
        :key="voucher.type"
        cols="12"
        md="6"
        xl="4"
      >
        <voucher :voucher="voucher" />
      </v-col>
    </v-row>
    <v-card
      v-else
      border
    >
      <no-content-screen
        :title="$t('partner.no-content-screen.title')"
        :sub-title="$t('partner.no-content-screen.subtitle')"
      />
    </v-card>
  </div>
</template>

<script>
import BaseHeader from '@/components/BaseHeader.vue'
import NoContentScreen from '@/components/NoContentScreen.vue'
import brandingMixin from '@/mixins/branding'

import COMPANY from './Company.gql'
import HeaderDesign from './HeaderDesign.vue'
import {
  PARTNER_ORDER,
  PARTNER_TAGS,
  PARTNER_TYPES,
  ReadablePartnerTags,
  STATIC_PARTNERS,
} from './helper/partner'
import Voucher from './Voucher'

export default {
  components: {
    Voucher,
    BaseHeader,
    NoContentScreen,
    HeaderDesign,
  },
  mixins: [brandingMixin],
  data() {
    return {
      filters: {
        [PARTNER_TAGS.ALL]: { isActive: true },
      },
      ReadablePartnerTags,
    }
  },
  apollo: {
    company: {
      query: COMPANY,
      variables() {
        return {
          id: this.$auth.user.companyId,
        }
      },
    },
  },
  computed: {
    offers() {
      return this.partnerForPlatform()
        .sort((a, b) => PARTNER_ORDER.indexOf(a.type) - PARTNER_ORDER.indexOf(b.type))
        .filter(
          (partner) =>
            this.filters[PARTNER_TAGS.ALL]?.isActive ||
            PARTNER_TYPES[this.platform][partner.type]?.tags.some(
              (tag) => this.filters[tag]?.isActive
            )
        )
    },
    filtersArray() {
      return Object.keys(this.filters)
    },
  },
  watch: {
    company() {
      if (this.company) {
        const availablePartners = this.partnerForPlatform().reduce(
          (arr, item) => arr.concat(item.type),
          []
        )
        const allPartnersCount = Object.keys(PARTNER_TYPES[this.platform]).reduce(
          (count, partner) => count + (availablePartners.includes(partner) ? 1 : 0),
          0
        )
        const tags = {
          [PARTNER_TAGS.ALL]: {
            isActive: true,
            count: allPartnersCount,
          },
        }
        Object.keys(PARTNER_TYPES[this.platform]).forEach((partner) => {
          if (availablePartners.includes(partner)) {
            PARTNER_TYPES[this.platform][partner].tags.forEach((tag) => {
              if (!tags[tag]) tags[tag] = { isActive: false, count: 1 }
              else tags[tag].count++
            })
          }
        })
        this.filters = Object.assign({}, this.filters, tags)
        this.setInitialFilter()
      }
    },
  },
  methods: {
    setInitialFilter() {
      const { query } = this.$route
      if (query.filters) {
        Object.keys(this.filters).forEach((key) => {
          if (query.filters.includes(key)) {
            this.toggleFilter(key)
          }
        })
      }
    },
    toggleFilter(tag) {
      if (tag === PARTNER_TAGS.ALL) {
        this.$tracking.event('Partner', 'Clicked', 'Filter', 'All')
        Object.keys(this.filters).forEach((key) => {
          this.filters[key].isActive = false
        })
        this.filters[PARTNER_TAGS.ALL].isActive = true
      } else {
        this.filters[PARTNER_TAGS.ALL].isActive = false
        this.filters[tag].isActive = !this.filters[tag].isActive

        if (this.filters[tag].isActive) this.$tracking.event('Partner', 'Clicked', 'Filter', tag)

        this.handleNoFilter()
      }
    },
    handleNoFilter() {
      if (Object.values(this.filters).every((value) => value.isActive === false)) {
        this.filters[PARTNER_TAGS.ALL].isActive = true
      }
    },
    partnerForPlatform() {
      const backendVouchers = this.company?.vouchers || []
      const backendTypes = new Set(backendVouchers.map((v) => v.type))
      // Exclude static partners whose type is already covered by a backend voucher to avoid duplicates
      const deduplicatedStaticPartners = STATIC_PARTNERS.filter((p) => !backendTypes.has(p.type))

      return [...backendVouchers, ...deduplicatedStaticPartners].filter((partner) => {
        // For static partners, check if the current platform is specified in their platforms array
        if (partner.platforms) {
          return (
            partner.platforms.includes(this.platform) &&
            Boolean(PARTNER_TYPES[this.platform][partner.type])
          )
        }
        // For company vouchers (dynamic partners), check if the partner type exists for the platform
        return Boolean(PARTNER_TYPES[this.platform][partner.type])
      })
    },
  },
}
</script>
